import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Columns, Combine, Copy, Edit, FileDown, FileText, Loader2, Plus, Search, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { grnService } from '@/services/grnService'
import type { GoodsReceiptNoteRow } from '../types'

const PAGE_SIZE = 10

type DataColumnKey =
  | 'code'
  | 'reference'
  | 'warehouse'
  | 'vendor'
  | 'orderCode'
  | 'date'
  | 'total'
  | 'convertToBill'

const DATA_COLUMNS: { key: DataColumnKey; label: string }[] = [
  { key: 'code', label: 'Code' },
  { key: 'reference', label: 'Reference' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'orderCode', label: 'Order Code' },
  { key: 'date', label: 'Date' },
  { key: 'total', label: 'Total' },
  { key: 'convertToBill', label: 'Convert to bill' },
]

export const GoodsReceiptNotesList: React.FC = () => {
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<GoodsReceiptNoteRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [convertingId, setConvertingId] = useState<number | null>(null)
  const [combinedBillSubmitting, setCombinedBillSubmitting] = useState(false)
  const [selectedGrnIds, setSelectedGrnIds] = useState<number[]>([])
  const [visibleColumns, setVisibleColumns] = useState<Record<DataColumnKey, boolean>>({
    code: true,
    reference: true,
    warehouse: true,
    vendor: true,
    orderCode: true,
    date: true,
    total: true,
    convertToBill: true,
  })

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await grnService.getAllGrn(branchId, PAGE_SIZE, pageNumber)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch (error: unknown) {
        setRows([])
        setTotalCount(0)
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || 'Failed to load goods receipt notes.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.warehouse, r.vendor, r.orderCode, r.date, r.total].some((x) =>
        String(x).toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const eligibleForCombined = useMemo(
    () => filtered.filter((r) => !r.isBillCreated && Number.isFinite(r.id) && r.id > 0),
    [filtered],
  )

  const eligibleIdSet = useMemo(() => new Set(eligibleForCombined.map((r) => r.id)), [eligibleForCombined])

  const allEligibleSelected =
    eligibleForCombined.length > 0 && eligibleForCombined.every((r) => selectedGrnIds.includes(r.id))

  useEffect(() => {
    setSelectedGrnIds((prev) => prev.filter((id) => eligibleIdSet.has(id)))
  }, [eligibleIdSet])

  const toggleSelectRow = (id: number) => {
    setSelectedGrnIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAllEligible = () => {
    if (allEligibleSelected) {
      const drop = new Set(eligibleForCombined.map((r) => r.id))
      setSelectedGrnIds((prev) => prev.filter((id) => !drop.has(id)))
    } else {
      setSelectedGrnIds((prev) => {
        const next = new Set(prev)
        for (const r of eligibleForCombined) next.add(r.id)
        return [...next]
      })
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(pageNumber * PAGE_SIZE, totalCount)

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB')
  }

  const fmtTotal = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : String(n))

  const visibleDataCount = Object.values(visibleColumns).filter(Boolean).length
  const tableColSpan = visibleDataCount + 2

  const toggleColumn = (key: DataColumnKey) =>
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Reference', 'Warehouse', 'Vendor', 'Order Code', 'Date', 'Total']
    const lines = filtered.map((r) =>
      [r.code, r.reference, r.warehouse, r.vendor, r.orderCode, r.date, fmtTotal(r.total)].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleDelete = async (r: GoodsReceiptNoteRow) => {
    if (!branchId || !r.id) {
      toast.error('Cannot delete this record.')
      return
    }
    const label = r.code?.trim() || r.reference?.trim() || `GRN #${r.id}`
    const ok = await showDeleteConfirm(label)
    if (!ok) return
    try {
      setDeletingId(r.id)
      await grnService.delete(r.id)
      await showSuccess('Deleted', 'Goods receipt note was deleted.')
      const res = await grnService.getAllGrn(branchId, PAGE_SIZE, pageNumber)
      setTotalCount(res.totalCount)
      setRows(res.items)
      if (res.items.length === 0 && pageNumber > 1) {
        setPageNumber((p) => Math.max(1, p - 1))
      }
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Failed to delete goods receipt note.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleConvertToBill = async (r: GoodsReceiptNoteRow) => {
    if (r.isBillCreated) {
      toast.error('This goods receipt note is already converted to a bill.')
      return
    }
    if (!branchId || !r.id || r.id <= 0) {
      toast.error('Cannot convert this record.')
      return
    }
    try {
      setConvertingId(r.id)
      await grnService.convertToBill(r.id)
      const res = await grnService.getAllGrn(branchId, PAGE_SIZE, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
      await showSuccess(
        'Converted to bill',
        'The goods receipt note was converted to a bill successfully.',
      )
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Failed to convert to bill.')
    } finally {
      setConvertingId(null)
    }
  }

  const handleCombinedBill = async () => {
    if (!branchId) {
      toast.error('Select a branch first.')
      return
    }
    const ids = selectedGrnIds.filter((id) => eligibleIdSet.has(id))
    if (ids.length === 0) {
      toast.error('Select at least one goods receipt note that is not yet converted to a bill.')
      return
    }
    try {
      setCombinedBillSubmitting(true)
      await grnService.combinedBill(ids)
      setSelectedGrnIds([])
      const res = await grnService.getAllGrn(branchId, PAGE_SIZE, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
      await showSuccess(
        'Combined bill',
        'The combined bill was created successfully. The list has been refreshed.',
      )
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Failed to create combined bill.')
    } finally {
      setCombinedBillSubmitting(false)
    }
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Reference,Warehouse,Vendor,OrderCode,Date,Total'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.warehouse, r.vendor, r.orderCode, r.date, fmtTotal(r.total)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `goods_receipt_notes_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Goods Receipt Notes</h1>
            <p className="text-muted-foreground text-sm">Goods Receipt Note Details</p>
          </div>
        </div>

        <Button onClick={() => navigate('/inventory/goods-receipt-notes/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Goods Receipt Note
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search (current page)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" type="button">
                    <Columns className="mr-2 h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {DATA_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleCombinedBill()}
                disabled={
                  combinedBillSubmitting ||
                  loading ||
                  !branchId ||
                  convertingId !== null ||
                  deletingId !== null ||
                  selectedGrnIds.length === 0
                }
              >
                {combinedBillSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Combine className="mr-2 h-4 w-4" />
                )}
                Combined bill{selectedGrnIds.length > 0 ? ` (${selectedGrnIds.length})` : ''}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  <TableHead className="h-12 w-[52px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={allEligibleSelected}
                        disabled={
                          eligibleForCombined.length === 0 ||
                          combinedBillSubmitting ||
                          loading ||
                          convertingId !== null ||
                          deletingId !== null
                        }
                        onCheckedChange={() => toggleSelectAllEligible()}
                        aria-label="Select all eligible goods receipt notes on this page"
                      />
                    </div>
                  </TableHead>
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Code
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Reference
                    </TableHead>
                  )}
                  {visibleColumns.warehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Warehouse
                    </TableHead>
                  )}
                  {visibleColumns.vendor && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Vendor
                    </TableHead>
                  )}
                  {visibleColumns.orderCode && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Order Code
                    </TableHead>
                  )}
                  {visibleColumns.date && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Date
                    </TableHead>
                  )}
                  {visibleColumns.total && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Total
                    </TableHead>
                  )}
                  {visibleColumns.convertToBill && (
                    <TableHead className="h-12 min-w-[9rem] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Convert to bill
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[100px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!branchId ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      Select a branch to load goods receipt notes.
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">Loading...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r, i) => {
                    const convertToBillDisabled =
                      convertingId !== null ||
                      deletingId !== null ||
                      r.id <= 0 ||
                      r.isBillCreated
                    const canSelectForCombined =
                      !r.isBillCreated && Number.isFinite(r.id) && r.id > 0
                    return (
                    <TableRow
                      key={r.id > 0 ? r.id : `row-${pageNumber}-${i}`}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      <TableCell className="w-[52px] px-2 py-2 text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={selectedGrnIds.includes(r.id)}
                            disabled={
                              !canSelectForCombined ||
                              combinedBillSubmitting ||
                              loading ||
                              convertingId !== null ||
                              deletingId !== null
                            }
                            onCheckedChange={() => toggleSelectRow(r.id)}
                            aria-label={`Select goods receipt note ${r.code || r.id}`}
                          />
                        </div>
                      </TableCell>
                      {visibleColumns.code && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.code || '-'}</TableCell>
                      )}
                      {visibleColumns.reference && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.reference || '-'}</TableCell>
                      )}
                      {visibleColumns.warehouse && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.warehouse || '-'}</TableCell>
                      )}
                      {visibleColumns.vendor && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.vendor || '-'}</TableCell>
                      )}
                      {visibleColumns.orderCode && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.orderCode || '-'}</TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.date)}</TableCell>
                      )}
                      {visibleColumns.total && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtTotal(r.total)}</TableCell>
                      )}
                      {visibleColumns.convertToBill && (
                        <TableCell className="px-2 py-2 text-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            disabled={convertToBillDisabled}
                            title={r.isBillCreated ? 'Already converted to bill' : undefined}
                            onClick={() => void handleConvertToBill(r)}
                          >
                            {convertingId === r.id ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                            ) : null}
                            Convert to bill
                          </Button>
                        </TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            disabled={convertToBillDisabled}
                            title={r.isBillCreated ? 'Already converted to bill' : 'Edit'}
                            aria-label="Edit"
                            onClick={() => navigate(`/inventory/goods-receipt-notes/${r.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            disabled={convertToBillDisabled}
                            title={r.isBillCreated ? 'Already converted to bill' : 'Delete'}
                            aria-label="Delete"
                            onClick={() => void handleDelete(r)}
                          >
                            {deletingId === r.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Showing {startIdx} to {endIdx} of {totalCount} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={!branchId || pageNumber <= 1 || loading}
              >
                Prev
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">
                {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={!branchId || pageNumber >= totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
